import { Icon } from '@buggy/shared'
import { Button, Flex, Input, Select } from 'antd'
import dayjs from 'dayjs'
import { head, isEmpty, isUndefined } from 'lodash-es'
import { DefaultOptionType } from 'rc-select/lib/Select'
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useWindowSize } from 'react-use'
import styled, { useTheme } from 'styled-components'
import AddPostToCollectionButton, {
  TriggerElement,
} from '@/components/collection/add-post-to-collection-button'
import AISwitch, { ChildWithAIBadge } from '@/components/common/ai-switch'
import Card from '@/components/common/post-card'
import PostSkeleton from '@/components/common/post-skeleton'
import PlatformSelector, {
  StyledPlatformSelector,
} from '@/components/common/selectors/platform-selector'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import AdvancedFilterDrawer from '@/components/kol/detail/creative-content-analyses/advanced-filter-drawer'
import PostListSearchTag from '@/components/kol/detail/creative-content-analyses/post-list-search-tag'
import { NoData } from '@/components/kol/detail/no-data'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import useKolPosts from '@/components/kol/hooks/use-kol-posts'
import SocialMediaPostModal, {
  SocialMediaPost,
} from '@/components/modal/social-media-post-modal'
import { Page } from '@/hooks/use-authorization/constants'
import {
  kolDetailFragmentIdentifierSchema,
  kolDetailPostListAdvancedFilterSchema,
  type KolDetailPostFilterConditions,
} from '@/hooks/use-authorization/routes'
import useCustomizedTag from '@/hooks/use-customized-tag'
import useHandleInputComposition from '@/hooks/use-handle-input-composition'
import useIsClient from '@/hooks/use-is-client'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Optional } from '@/types/optional'
import {
  KolPostListSorter,
  kolPostListSorterSchema,
} from '@/types/schema/api/kol'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'
import {
  convertPlatformType,
  Platform,
  PlatformShortcode,
} from '@/utils/convert-platform'
import getKolPlatformDatum from '@/utils/kol/get-kol-platform-datum'
import getPostThumbnailUrls from '@/utils/kol/get-post-thumbnail-urls'

interface PostListProps {
  anchorOffset: number
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  kolInfo?: KolSchemaModel
  platform: Platform
}

export const initialPostFilterConditions: KolDetailPostFilterConditions = {
  postListCustomizedTagsNames: [],
  postListPostTypes: [],
  postListDateRangeStart: undefined,
  postListDateRangeEnd: undefined,
  keyword: undefined,
  postListSort: 'publishTime',
  postListShowSponsoredPost: undefined,
  postListNextPagingToken: undefined,
  postListPlatform: undefined,
}

const PostList: FunctionComponent<PostListProps> = ({
  anchorOffset,
  isFetchingKolInfo,
  isKolInfoLocked,
  kolInfo,
  platform,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { width } = useWindowSize()
  const isClient = useIsClient()
  const containerRef = useRef<HTMLDivElement>(null)
  const { page, searchParams, setSearchParams } = useKolDetailRoute()
  const { handleInputComposition } = useHandleInputComposition(
    async (): Promise<void> => {
      await handleClickSearch()
    },
  )

  const [selectedPost, setSelectedPost] = useState<SocialMediaPost>()
  const [keyword, setKeyword] = useState<string>()
  const [advancedFilterDrawerOpen, setAdvancedFilterDrawerOpen] =
    useState(false)
  const { searchCustomizedTagNames, getCustomizedTagLocalizedName } =
    useCustomizedTag()
  const { response: kolPostsLost, isFetching: isFetchingKolPosts } =
    useKolPosts({
      kolId: kolInfo?.id,
      kolDcIdHashed: kolInfo?.dcIdHashed,
      platform:
        platform.shortcode === PlatformShortcode.All
          ? undefined
          : platform.shortcode,
      request: {
        keyword: searchParams.data?.keyword,
        sort: searchParams.data?.postListSort,
        is_advertorial: searchParams.data?.postListShowSponsoredPost,
        next_paging_token: searchParams.data?.postListNextPagingToken,
        customized_tags_names: searchParams.data?.postListCustomizedTagsNames,
        post_types: searchParams.data?.postListPostTypes,
        postStartTime: searchParams.data?.postListDateRangeStart,
        postEndTime: searchParams.data?.postListDateRangeEnd,
        size: ((): number => {
          if (isKolInfoLocked) {
            return width <= 980 ? 3 : 4
          }

          return 12
        })(),
      },
    })

  const getLockableLabel = (textId: I18nId): ReactNode => {
    return (
      <Flex align='center' gap={4}>
        {isKolInfoLocked && <Icon name='lock' />}
        {formatMessage({ id: textId })}
      </Flex>
    )
  }

  const platforms = kolInfo ? getKolPlatformDatum(kolInfo) : []
  const sorterOptions: DefaultOptionType[] = [
    {
      value: kolPostListSorterSchema.enum.publishTime,
      label: formatMessage({ id: 'kol:content_sort_option_updated' }),
    },
    {
      value: kolPostListSorterSchema.enum.engagement,
      label: getLockableLabel('kol:post_option_engagement'),
      disabled: isKolInfoLocked,
    },
    {
      value: kolPostListSorterSchema.enum.pageView,
      label: getLockableLabel('kol:post_option_view'),
      disabled: isKolInfoLocked,
    },
    {
      value: kolPostListSorterSchema.enum.likeCount,
      label: getLockableLabel('autoreport:chart_field_like'),
      disabled: isKolInfoLocked,
    },
    {
      value: kolPostListSorterSchema.enum.commentCount,
      label: getLockableLabel('autoreport:chart_field_comment'),
      disabled: isKolInfoLocked,
    },
  ]
  const isFetching = isFetchingKolInfo || isFetchingKolPosts
  const suggestCustomizedTagName = useMemo((): Optional<string> => {
    if (isKolInfoLocked || !searchParams.data?.keyword) {
      return undefined
    }

    return head(searchCustomizedTagNames(searchParams.data.keyword))
  }, [isKolInfoLocked, searchCustomizedTagNames, searchParams.data?.keyword])

  const handleClickSearch = async (): Promise<void> => {
    sendAmpliSearchCreativeContentInKd({
      keyword,
    })

    await setSearchParams(
      {
        ...searchParams.data,
        postListNextPagingToken: undefined,
        keyword,
      },
      'postList',
    )
  }
  const handleClickClearAdvancedFilter = async (): Promise<void> => {
    await setSearchParams(
      {
        ...searchParams.data,
        ...Object.fromEntries(
          Object.entries(
            kolDetailPostListAdvancedFilterSchema.keyof().options,
          ).map(([, value]) => [value, undefined]),
        ),
        postListNextPagingToken: undefined,
      },
      'postList',
    )
  }

  const handleClickTag = async (tagName: string): Promise<void> => {
    await setSearchParams(
      {
        ...searchParams.data,
        postListCustomizedTagsNames: [tagName],
      },
      'postList',
    )
  }

  useEffect((): void => {
    if (keyword !== searchParams.data?.keyword) {
      setKeyword(searchParams.data?.keyword)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.data?.keyword])

  //  發送 Ampli 的事件相關欄位，只傳送有變更的篩選條件
  const sendAmpliSearchCreativeContentInKd = (
    data: Partial<KolDetailPostFilterConditions>,
  ): void => {
    const eventSourceData = {
      ...searchParams.data,
      ...data,
      postListPlatform: data.postListPlatform ?? platform.shortcode,
    }

    const event = {
      topicTypes: eventSourceData.postListCustomizedTagsNames,
      keyword: eventSourceData.keyword,
      aiEnabled: !isUndefined(eventSourceData.postListShowSponsoredPost),
      platform:
        eventSourceData.postListPlatform === PlatformShortcode.All
          ? 'All'
          : eventSourceData.postListPlatform,
      postTypes: eventSourceData.postListPostTypes,
      postStartDate: eventSourceData.postListDateRangeStart,
      postEndDate: eventSourceData.postListDateRangeEnd,
    }

    ampli.searchForCreativeContentInKd(event)
  }

  return (
    <KolDetailDataBlock
      colSpan={24}
      header={{
        anchor: {
          id: kolDetailFragmentIdentifierSchema.enum.postList,
          offset: anchorOffset,
        },
        title: formatMessage({
          id: 'kol:content_list_data_block_title',
        }),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Contents'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
      ref={containerRef}
    >
      <Flex vertical gap={20}>
        <Flex justify='space-between'>
          <Flex gap={20}>
            <Flex gap={10}>
              <PlatformSelectWrapper>
                <PlatformSelector
                  hasAllOption
                  showIcon
                  availablePlatforms={platforms.map(
                    (platformData) => platformData.shortcode,
                  )}
                  iconSize={20}
                  size='middle'
                  value={platform.shortcode}
                  onChange={async (platformShortcode): Promise<void> => {
                    ampli.changeContentPlatform({
                      from:
                        page === Page.KolId
                          ? searchParams.data?.from
                          : undefined,
                      platform:
                        platformShortcode === PlatformShortcode.All
                          ? 'All'
                          : platformShortcode,
                    })

                    sendAmpliSearchCreativeContentInKd({
                      ...Object.fromEntries(
                        Object.entries(
                          kolDetailPostListAdvancedFilterSchema.keyof().options,
                        ).map(([, value]) => [value, undefined]),
                      ),
                      postListPlatform: platformShortcode,
                    })

                    await setSearchParams(
                      {
                        ...searchParams.data,
                        ...Object.fromEntries(
                          Object.entries(
                            kolDetailPostListAdvancedFilterSchema.keyof()
                              .options,
                          ).map(([, value]) => [value, undefined]),
                        ),
                        postListNextPagingToken: undefined,
                        postListPlatform: platformShortcode,
                      },
                      'postList',
                    )
                  }}
                />
              </PlatformSelectWrapper>
              <Input
                {...handleInputComposition}
                allowClear
                disabled={isKolInfoLocked}
                placeholder={formatMessage({ id: 'search:input_keyword' })}
                size='small'
                suffix={
                  <SearchButtonWrapper>
                    <Button
                      disabled={isKolInfoLocked}
                      icon={
                        <Icon
                          color={
                            isKolInfoLocked
                              ? theme.colors.text.placeholder
                              : theme.colors.brand.primary
                          }
                          fontSize={16}
                          name='search'
                        />
                      }
                      type='link'
                      onClick={handleClickSearch}
                    />
                  </SearchButtonWrapper>
                }
                value={keyword}
                onChange={async (event): Promise<void> => {
                  if (searchParams.data?.keyword && !event.target.value) {
                    await setSearchParams(
                      {
                        ...searchParams.data,
                        keyword: undefined,
                      },
                      'postList',
                    )
                  } else {
                    setKeyword(event.target.value)
                  }
                }}
              />
            </Flex>
            <AdvancedFilterButton
              icon={isKolInfoLocked ? <Icon name='lock' /> : undefined}
              size='small'
              type='link'
              onClick={(): void => {
                setAdvancedFilterDrawerOpen(true)
                ampli.clickAdvancedFilterOnAllContent({
                  isUnlocked: !isKolInfoLocked,
                  kolId: kolInfo?.id,
                  kolName: kolInfo?.name,
                })
              }}
            >
              {formatMessage({ id: 'search:advanced_title' })}
            </AdvancedFilterButton>
            {suggestCustomizedTagName && (
              <SuggestCustomizedTagButton
                size='small'
                type='link'
                onClick={async (): Promise<void> => {
                  sendAmpliSearchCreativeContentInKd({
                    postListCustomizedTagsNames: [suggestCustomizedTagName],
                    keyword: undefined,
                  })

                  await setSearchParams(
                    {
                      ...searchParams.data,
                      postListCustomizedTagsNames: [suggestCustomizedTagName],
                      keyword: undefined,
                    },
                    'postList',
                  )
                }}
              >
                {formatMessage(
                  { id: 'kol:content_customized_tag_suggestion' },
                  {
                    customizedTagName: getCustomizedTagLocalizedName(
                      suggestCustomizedTagName,
                    ),
                  },
                )}
              </SuggestCustomizedTagButton>
            )}
          </Flex>
          <Flex align='center' gap={16}>
            <AIIndicatesWrapper align='center' gap={8}>
              <AISwitch
                checkedChildren={
                  <ChildWithAIBadge>
                    {formatMessage({ id: 'general:enable' })}
                  </ChildWithAIBadge>
                }
                disabled={isKolInfoLocked}
                unCheckedChildren={
                  <ChildWithAIBadge>
                    {formatMessage({ id: 'general:disable' })}
                  </ChildWithAIBadge>
                }
                value={!!searchParams.data?.postListShowSponsoredPost}
                onChange={async (checked): Promise<void> => {
                  sendAmpliSearchCreativeContentInKd({
                    postListShowSponsoredPost: checked ? true : undefined,
                  })

                  await setSearchParams(
                    {
                      ...searchParams.data,
                      postListNextPagingToken: undefined,
                      postListShowSponsoredPost: checked ? true : undefined,
                    },
                    'postList',
                  )
                }}
              />
              {getLockableLabel('kol:content_ai_indicates_advertorial')}
            </AIIndicatesWrapper>
            <SorterSelect
              labelRender={({ label }): ReactNode => {
                if (!label) {
                  return null
                }

                return (
                  <SelectedOption>
                    {formatMessage(
                      {
                        id: 'kol:post_list_sort_label',
                      },
                      { optionLabel: label },
                    )}
                  </SelectedOption>
                )
              }}
              options={sorterOptions}
              placeholder={formatMessage({
                id: 'search:sorting_before_options_label',
              })}
              popupMatchSelectWidth={false}
              value={searchParams.data?.postListSort}
              onSelect={async (value: KolPostListSorter): Promise<void> => {
                await setSearchParams(
                  {
                    ...searchParams.data,
                    postListNextPagingToken: undefined,
                    postListSort: value,
                  },
                  'postList',
                )
              }}
            />
          </Flex>
        </Flex>
        <PostListSearchTag totalCount={kolPostsLost?.paging.total ?? 0} />
        {!isFetching && isEmpty(kolPostsLost?.data) ? (
          <NoData />
        ) : (
          <Flex vertical align='center' gap={20}>
            <List>
              {kolInfo &&
                !(isFetching && !searchParams.data?.postListNextPagingToken) &&
                kolPostsLost?.data.map((post) => {
                  const postPlatform = convertPlatformType(post.platformType)

                  return (
                    <Card
                      actionBlock={
                        <AddPostToCollectionButton
                          containerRef={containerRef}
                          from='KOL Detail'
                          kol={{
                            id: kolInfo.id,
                            name: kolInfo.name,
                            potential: kolInfo.potential,
                            customizedTagsNames: kolInfo.customizedTagsNames,
                          }}
                          post={{
                            id: post.postID,
                            platformType: post.platformType,
                            url: post.url,
                            postType: post.postType,
                            isCollectionPost: post.isCollectionPost,
                          }}
                          type='text'
                        />
                      }
                      commentCount={post.commentCount}
                      customizedTagNames={post.customizedTagsNames}
                      estimatedReachCount={post.estimatedReachCount}
                      isAdvertorial={post.isAdvertorial}
                      isDisableLike={post.isDisableLike}
                      key={post.postID}
                      keywords={kolPostsLost?.keywords}
                      kolName={kolInfo.name}
                      kolUuid={kolInfo.id}
                      likeCount={post.likeCount}
                      platformShortCode={postPlatform?.shortcode}
                      playCount={post.playCount}
                      postUrl={post.url}
                      publishedAt={post.publishTime}
                      shareCount={post.shareCount}
                      showKolInfo={false}
                      thumbnailUrls={getPostThumbnailUrls(post)}
                      title={post.title.trim() + post.text.trim()}
                      titleLineClamp={2}
                      viewCount={post.viewCount}
                      onPostClick={(): void => {
                        if (!postPlatform) {
                          return
                        }

                        setSelectedPost({
                          crawledID: post.crawledID,
                          platformCode: postPlatform.code,
                          platformTitle: postPlatform.title,
                          postID: post.postID,
                          postType: post.postType,
                          url: post.url,
                          kolId: kolInfo.id,
                          kolDcIdHashed: kolInfo.dcIdHashed,
                          kolName: kolInfo.name,
                        })
                      }}
                      onTagClick={handleClickTag}
                    />
                  )
                })}
              {isFetching &&
                isClient &&
                Array.from({ length: width <= 980 ? 3 : 4 }, (_, index) => {
                  return <PostSkeleton key={`post-skeleton-${index}`} />
                })}
            </List>
            {!isKolInfoLocked && !isEmpty(kolPostsLost?.paging.next) && (
              <Button
                icon={<Icon name='chevrons-down' />}
                loading={
                  isFetchingKolPosts &&
                  !isUndefined(searchParams.data?.postListNextPagingToken)
                }
                size='large'
                type='primary'
                onClick={async (): Promise<void> => {
                  await setSearchParams({
                    ...searchParams.data,
                    postListNextPagingToken: kolPostsLost?.paging.next,
                  })
                }}
              >
                {formatMessage({
                  id: 'kol:post_list_load_more_act',
                })}
              </Button>
            )}
          </Flex>
        )}
        {selectedPost && (
          <SocialMediaPostModal
            containerRef={containerRef}
            from='KOL Detail'
            post={selectedPost}
            onCancel={(): void => setSelectedPost(undefined)}
          />
        )}
        <AdvancedFilterDrawer
          containerRef={containerRef}
          data={{
            customizedTagsNames: searchParams.data?.postListCustomizedTagsNames,
            postTypes: searchParams.data?.postListPostTypes,
            postDateRange:
              searchParams.data?.postListDateRangeStart &&
              searchParams.data?.postListDateRangeEnd
                ? [
                    dayjs(searchParams.data?.postListDateRangeStart),
                    dayjs(searchParams.data?.postListDateRangeEnd),
                  ]
                : undefined,
          }}
          isKolInfoLocked={isKolInfoLocked}
          open={advancedFilterDrawerOpen}
          platform={platform.shortcode}
          onClickApply={async (filterData): Promise<void> => {
            sendAmpliSearchCreativeContentInKd({
              postListCustomizedTagsNames: filterData.customizedTagsNames,
              postListPostTypes: filterData.postTypes,
              postListDateRangeStart: filterData.postDateRange?.[0]
                ?.startOf('day')
                .toISOString(),
              postListDateRangeEnd: filterData.postDateRange?.[1]
                ?.endOf('day')
                .toISOString(),
            })

            await setSearchParams(
              {
                ...searchParams.data,
                postListNextPagingToken: undefined,
                postListCustomizedTagsNames: filterData.customizedTagsNames,
                postListPostTypes: filterData.postTypes,
                postListDateRangeStart: filterData.postDateRange?.[0]
                  ?.startOf('day')
                  .toISOString(),
                postListDateRangeEnd: filterData.postDateRange?.[1]
                  ?.endOf('day')
                  .toISOString(),
              },
              'postList',
            )

            setAdvancedFilterDrawerOpen(false)
          }}
          onClickClear={handleClickClearAdvancedFilter}
          onClose={(): void => setAdvancedFilterDrawerOpen(false)}
        />
      </Flex>
    </KolDetailDataBlock>
  )
}

const PlatformSelectWrapper = styled.div`
  ${StyledPlatformSelector} {
    flex-shrink: 0;
  }
`

const SearchButtonWrapper = styled.div`
  line-height: 16px;

  .ant-btn.ant-btn-icon-only:not(.ant-btn-sm):not(.ant-btn-block) {
    padding: 0;
    width: 16px;
    height: 16px;
    font-size: 16px;
  }
`

const AdvancedFilterButton = styled(Button)`
  padding: 0;
  span {
    text-decoration-line: underline;
    text-underline-offset: 2px;
  }
`

const SuggestCustomizedTagButton = styled(Button)`
  padding: 0;
`

const SorterSelect = styled(Select)`
  min-width: 142px;
`

const SelectedOption = styled.div`
  display: flex;
  white-space: break-spaces;
`

const AIIndicatesWrapper = styled(Flex)`
  padding: 0 10px 0 0;
  border-right: 1px solid ${({ theme }): string => theme.colors.border.border};
`

const List = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px 24px;

  ${Card} {
    overflow: hidden;
    box-shadow: none;
    border-radius: 4px;
    border: 1px solid ${({ theme }): string => theme.colors.border.divider};

    &:hover {
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    }
  }

  ${TriggerElement} {
    span {
      font-size: 20px;
    }
  }

  @media (min-width: 981px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`

export default PostList
